import type {
  ApiSettings,
  CatalogFacets,
  CatalogFilterSelection,
  CatalogResponse,
  DiscoveryResponse,
  DiscoveryMovieDetail,
  MetadataSearchResponse,
  MovieDetail,
  Release,
  ReleaseSearchResponse,
  WatchProgress,
  CybertubeAccountStatus,
  CybertubeCatalogResponse,
  CybertubeLinkStart,
  CybertubeLinkStatus,
  CybertubePlaylistResponse,
  CybertubeProgress,
  CybertubeVideo,
  CybertubeWatch,
  IptvChannelDetail,
  IptvChannelKind,
  IptvChannelPage,
  IptvGroup,
  IptvPlaybackPlan,
  IptvSource,
  FrontendFeatures,
} from "./types";
import type { AccountSession } from "./authContext";
import type { SeriesCard, SeriesPage, SeriesDetail as CatalogSeriesDetail, SeasonDetail, EpisodeDetail, EpisodePlayback, SeriesFollow, SeriesNotification } from './series-types';

export class DistributionApiError extends Error {
  readonly status: number;
  readonly detail?: unknown;

  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "DistributionApiError";
    this.status = status;
    this.detail = detail;
  }
}

export class DistributionClient {
  private readonly base: string;
  private readonly settings: ApiSettings;
  private readonly session: AccountSession;
  private readonly seriesCache = new Map<string, { etag: string; body: unknown }>();

  // Plain fields, not parameter properties: node --test runs this file with type stripping.
  constructor(settings: ApiSettings, session: AccountSession) {
    this.settings = settings;
    this.session = session;
    this.base = settings.apiBase.replace(/\/+$/, "");
  }

  eventsUrl(): string { return `${this.base}/events`; }

  reportFirstFrame(target: { path: string; publicationId: string }, elapsedMs: number) {
    return this.request(target.path.replace(/^\/distribution/, ''), { method: 'POST', body: JSON.stringify({
      publicationId: target.publicationId, elapsedMs, measurement: 'request_video_frame',
    }) });
  }
  /** The /events socket needs the session token, as the HTTP routes do. */
  eventsAuth(): { token: string; deviceId: string } { return { token: this.session.accessToken, deviceId: this.session.deviceId }; }

  audioLanguages(): Promise<Array<{ code: string; name: string }>> { return this.request('/audio-languages'); }

  features(): Promise<FrontendFeatures> {
    return this.request('/features');
  }

  private async request<T>(path: string, init?: RequestInit, seriesCacheKey?: string): Promise<T> {
    const url = new URL(`${this.base}/distribution${path}`);
    url.searchParams.set("lang", this.settings.locale);
    // Cyber Movies uses a regular media element (and the TV builds use their
    // native player). The `web` profile is reserved for Cyberdash WebCodecs.
    url.searchParams.set("profile", "native");
    const headers = new Headers(init?.headers);
    headers.set("Accept", "application/json");
    if (init?.body) headers.set("Content-Type", "application/json");
    headers.set("Authorization", `Bearer ${this.session.accessToken}`);
    headers.set("X-Device-ID", this.session.deviceId);
    const cached = seriesCacheKey ? this.seriesCache.get(seriesCacheKey) : undefined;
    if (cached) headers.set('If-None-Match', cached.etag);

    let response = await fetch(url, { ...init, headers });
    if ((init?.method === undefined || init.method === 'GET') && (response.status === 502 || response.status === 503)) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      response = await fetch(url, { ...init, headers });
    }
    if (response.status === 304 && cached) return cached.body as T;
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        message?: unknown;
      } | null;
      const message =
        response.status === 401
          ? "Please sign in again."
          : response.status === 403
            ? "This title is unavailable for this account."
            : response.status === 404 || response.status === 400
              ? "This movie is temporarily unavailable."
              : "Something went wrong. Please try again.";
      throw new DistributionApiError(
        message,
        response.status,
        payload?.message,
      );
    }
    const body = await response.json() as T;
    const etag = response.headers.get('ETag');
    if (seriesCacheKey && etag) this.seriesCache.set(seriesCacheKey, { etag, body });
    return body;
  }

  catalog(query = ""): Promise<CatalogResponse> {
    return this.request(
      `/movies${query ? `?q=${encodeURIComponent(query)}` : ""}`,
    );
  }

  /** The genres and streaming services the catalog filter offers, counted for the current selection. */
  catalogFacets(kind: 'movie' | 'series', selection: CatalogFilterSelection): Promise<CatalogFacets> {
    const params = new URLSearchParams({ kind });
    if (selection.genreIds.length) params.set('genre', selection.genreIds.join(','));
    if (selection.providerIds.length) params.set('provider', selection.providerIds.join(','));
    return this.request(`/catalog/facets?${params}`);
  }

  trending(): Promise<DiscoveryResponse> {
    return this.request("/discovery/trending");
  }

  upcomingDiscovery(): Promise<DiscoveryResponse> {
    return this.request("/discovery/upcoming");
  }

  discoveryMovie(tmdbId: string): Promise<DiscoveryMovieDetail> {
    return this.request(`/discovery/movie/${encodeURIComponent(tmdbId)}`);
  }

  metadataSearch(query: string): Promise<MetadataSearchResponse> {
    return this.request(`/metadata/search?q=${encodeURIComponent(query)}`);
  }

  movie(idOrSlug: string): Promise<MovieDetail> {
    return this.request(`/movies/${encodeURIComponent(idOrSlug)}`);
  }

  seriesShelf(cursor?: string, filter?: CatalogFilterSelection): Promise<SeriesPage<SeriesCard>> {
    const params = new URLSearchParams({ shelf: 'recent' });
    if (cursor) params.set('cursor', cursor);
    if (filter?.genreIds.length) params.set('genre', filter.genreIds.join(','));
    if (filter?.providerIds.length) params.set('provider', filter.providerIds.join(','));
    return this.request(`/series?${params}`);
  }
  seriesContinue(): Promise<{ items: Array<{ series: SeriesCard; episode: { id: string; title: string; playable: boolean } | null }> }> {
    return this.request('/series/continue');
  }
  seriesLookup(tmdbIds: number[]): Promise<{ items: Array<{ tmdbId: number; seriesId: string; published: boolean }> }> {
    return this.request(`/series/lookup?tmdbIds=${tmdbIds.slice(0, 50).join(',')}`);
  }
  seriesByTmdb(tmdbId: string): Promise<CatalogSeriesDetail> {
    return this.request(`/series/tmdb/${encodeURIComponent(tmdbId)}`);
  }
  seriesById(id: string): Promise<CatalogSeriesDetail> { return this.request(`/series/${id}`); }
  seriesSeason(id: string, number: number): Promise<SeasonDetail> {
    return this.request(`/series/${id}/seasons/${number}`, undefined, `season:${id}:${number}:${this.settings.locale}`);
  }
  seriesSeasonStatus(id: string, number: number): Promise<{ published: boolean; episodes: Array<{ id: string; availability: SeasonDetail['episodes'][number]['availability'] }> }> {
    const language = this.settings.preferredAudioLanguage ?? 'eng';
    return this.request(`/series/${id}/seasons/${number}/status?preferredLanguage=${encodeURIComponent(language)}`, undefined, `status:${id}:${number}:${language}`);
  }
  seriesEpisode(id: string): Promise<EpisodeDetail> { return this.request(`/series/episodes/${id}`); }
  episodePlayback(id: string): Promise<EpisodePlayback> { return this.request(`/series/episodes/${id}/playback`); }
  nextSeriesEpisode(id: string): Promise<{ next: EpisodeDetail | null }> { return this.request(`/series/episodes/${id}/next`); }
  acquireSeriesSeason(id: string, number: number): Promise<unknown> {
    return this.request(`/series/${id}/seasons/${number}/acquire`, { method: 'POST', body: JSON.stringify({ preferredLanguage: this.settings.preferredAudioLanguage ?? 'eng' }) });
  }
  acquireSeriesEpisode(id: string): Promise<unknown> {
    return this.request(`/series/episodes/${id}/acquire`, { method: 'POST', body: JSON.stringify({ preferredLanguage: this.settings.preferredAudioLanguage ?? 'eng' }) });
  }
  setSeriesFollow(id: string, flags: SeriesFollow): Promise<SeriesFollow> {
    return this.request(`/series/${id}/follow`, { method: 'PUT', body: JSON.stringify(flags) });
  }
  unsetSeriesFollow(id: string): Promise<unknown> { return this.request(`/series/${id}/follow`, { method: 'DELETE' }); }
  mySeries(cursor?: string): Promise<SeriesPage<SeriesCard>> {
    return this.request(`/series/mine${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
  }
  seriesNotifications(cursor?: string): Promise<SeriesPage<SeriesNotification>> {
    return this.request(`/series/notifications${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`);
  }
  seriesUnreadCount(): Promise<{ count: number }> { return this.request('/series/notifications/unread-count'); }
  markSeriesNotificationsRead(ids?: string[]): Promise<{ count: number }> {
    return this.request('/series/notifications/read', { method: 'POST', body: JSON.stringify(ids ? { ids } : { all: true }) });
  }
  saveEpisodeProgress(id: string, currentTime: number, duration: number): Promise<unknown> {
    return this.request(`/series/episodes/${id}/progress`, { method: 'POST', body: JSON.stringify({ currentTime, duration }) });
  }

  status(idOrSlug: string): Promise<MovieDetail> {
    return this.request(`/movies/${encodeURIComponent(idOrSlug)}/status`);
  }

  searchReleases(
    query: string,
    movieId?: string,
    tmdbId?: string,
  ): Promise<ReleaseSearchResponse> {
    return this.request("/search", {
      method: "POST",
      body: JSON.stringify({
        query,
        ...(movieId ? { movieId } : {}),
        ...(tmdbId ? { tmdbId } : {}),
      }),
    });
  }

  acquire(
    query: string,
    release: Release,
    movie: {
      title: string;
      year: number | null;
      tmdbId?: string | null;
      type?: "movie";
      description?: string | null;
      poster?: string | null;
      backdrop?: string | null;
    },
    fallbacks: Release[] = [],
  ) {
    return this.request<{ movieId: string }>("/search/acquire", {
      method: "POST",
      body: JSON.stringify({
        query,
        release,
        fallbacks,
        download: true,
        preferredLanguage: this.settings.preferredAudioLanguage ?? 'eng',
        movie,
      }),
    });
  }

  watchProgress(): Promise<WatchProgress[]> {
    return this.request("/progress");
  }

  saveWatchProgress(progress: WatchProgress): Promise<WatchProgress | null> {
    const conn =
      typeof navigator !== "undefined"
        ? (navigator as Navigator & {
            connection?: { downlink?: number; rtt?: number; effectiveType?: string };
          }).connection
        : undefined;
    const throughputKbps =
      Number.isFinite(progress.throughputKbps) && (progress.throughputKbps as number) > 0
        ? Math.round(progress.throughputKbps as number)
        : undefined;
    return this.request(`/progress/${encodeURIComponent(progress.movieId)}`, {
      method: "PUT",
      body: JSON.stringify({
        currentTime: progress.currentTime,
        duration: progress.duration,
        client: "cyber_movies",
        ...(typeof conn?.downlink === "number" ? { downlinkMbps: conn.downlink } : {}),
        ...(typeof conn?.rtt === "number" ? { rttMs: conn.rtt } : {}),
        ...(typeof conn?.effectiveType === "string"
          ? { effectiveType: conn.effectiveType }
          : {}),
        ...(throughputKbps ? { throughputKbps } : {}),
      }),
    });
  }

  clearWatchProgress(movieId: string): Promise<{ removed: boolean }> {
    return this.request(`/progress/${encodeURIComponent(movieId)}`, {
      method: "DELETE",
    });
  }

  // ------------------------------------------------------------- CyberTube

  cybertubeHome(limit = 24): Promise<CybertubeCatalogResponse> {
    return this.request(`/cybertube/home?limit=${limit}`);
  }

  cybertubeTrending(limit = 24): Promise<CybertubeCatalogResponse> {
    return this.request(`/cybertube/trending?limit=${limit}`);
  }

  cybertubeSearch(
    query: string,
    limit = 24,
  ): Promise<CybertubeCatalogResponse> {
    return this.request(
      `/cybertube/search?q=${encodeURIComponent(query)}&limit=${limit}`,
    );
  }

  cybertubeSuggest(query: string): Promise<{ items: string[] }> {
    return this.request(`/cybertube/suggest?q=${encodeURIComponent(query)}`);
  }

  cybertubeSubscriptions(limit = 24): Promise<CybertubeCatalogResponse> {
    return this.request(`/cybertube/subscriptions?limit=${limit}`);
  }

  cybertubeHistory(limit = 24): Promise<CybertubeCatalogResponse> {
    return this.request(`/cybertube/history?limit=${limit}`);
  }

  cybertubePlaylists(limit = 30): Promise<CybertubePlaylistResponse> {
    return this.request(`/cybertube/playlists?limit=${limit}`);
  }

  cybertubePlaylistItems(
    playlistId: string,
    limit = 50,
  ): Promise<CybertubeCatalogResponse> {
    return this.request(
      `/cybertube/playlists/${encodeURIComponent(playlistId)}?limit=${limit}`,
    );
  }

  /** Video details, related videos and the playback plan in one round trip. */
  cybertubeWatch(sourceId: string, height = 720): Promise<CybertubeWatch> {
    return this.request(
      `/cybertube/watch/${encodeURIComponent(sourceId)}?height=${height}`,
    );
  }

  /**
   * The `<video>` src. A media element cannot send headers, so the access token
   * and device id travel as query parameters — the API accepts both there.
   */
  cybertubeStreamUrl(plan: { url: string }, startSec = 0): string {
    const url = new URL(plan.url);
    url.searchParams.set("token", this.session.accessToken);
    url.searchParams.set("deviceId", this.session.deviceId);
    if (startSec > 0)
      url.searchParams.set("start", String(Math.floor(startSec)));
    return url.toString();
  }

  cybertubeAccount(): Promise<CybertubeAccountStatus> {
    return this.request("/cybertube/account");
  }

  startCybertubeLink(): Promise<CybertubeLinkStart> {
    return this.request("/cybertube/account/link", { method: "POST" });
  }

  cybertubeLinkStatus(): Promise<CybertubeLinkStatus> {
    return this.request("/cybertube/account/link");
  }

  unlinkCybertube(): Promise<{ unlinked: boolean }> {
    return this.request("/cybertube/account", { method: "DELETE" });
  }

  cybertubeLibrary(): Promise<{ count: number; items: CybertubeVideo[] }> {
    return this.request("/cybertube/library");
  }

  cybertubeVideo(id: string): Promise<CybertubeVideo> {
    return this.request(`/cybertube/videos/${encodeURIComponent(id)}`);
  }

  cybertubeProgress(): Promise<CybertubeProgress[]> {
    return this.request("/cybertube/progress");
  }

  saveCybertubeProgress(
    sourceId: string,
    progress: Pick<CybertubeProgress, "currentTime" | "duration">,
  ): Promise<CybertubeProgress | null> {
    return this.request(`/cybertube/progress/${encodeURIComponent(sourceId)}`, {
      method: "PUT",
      body: JSON.stringify({
        currentTime: progress.currentTime,
        duration: progress.duration,
      }),
    });
  }

  clearCybertubeProgress(sourceId: string): Promise<{ removed: boolean }> {
    return this.request(`/cybertube/progress/${encodeURIComponent(sourceId)}`, {
      method: "DELETE",
    });
  }

  // ------------------------------------------------------------------- IPTV

  iptvSources(): Promise<{ count: number; items: IptvSource[] }> {
    return this.request("/iptv/sources");
  }

  addIptvPlaylist(
    url: string,
    name?: string,
    epgUrl?: string,
  ): Promise<IptvSource> {
    return this.request("/iptv/sources", {
      method: "POST",
      body: JSON.stringify({ kind: "m3u", url, name, epgUrl }),
    });
  }

  addIptvXtream(input: {
    host: string;
    username: string;
    password: string;
    name?: string;
    epgUrl?: string;
  }): Promise<IptvSource> {
    return this.request("/iptv/sources", {
      method: "POST",
      body: JSON.stringify({ kind: "xtream", ...input }),
    });
  }

  removeIptvSource(id: string): Promise<{ removed: boolean }> {
    return this.request(`/iptv/sources/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }

  refreshIptvSource(id: string): Promise<IptvSource> {
    return this.request(`/iptv/sources/${encodeURIComponent(id)}/refresh`, {
      method: "POST",
    });
  }

  iptvChannels(query: {
    sourceId?: string;
    kind?: IptvChannelKind;
    group?: string;
    q?: string;
    favorites?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<IptvChannelPage> {
    const params = new URLSearchParams();
    if (query.sourceId) params.set("sourceId", query.sourceId);
    if (query.kind) params.set("kind", query.kind);
    if (query.group) params.set("group", query.group);
    if (query.q) params.set("q", query.q);
    if (query.favorites) params.set("favorites", "1");
    params.set("limit", String(query.limit ?? 60));
    params.set("offset", String(query.offset ?? 0));
    return this.request(`/iptv/channels?${params.toString()}`);
  }

  iptvGroups(
    sourceId?: string,
    kind?: IptvChannelKind,
  ): Promise<{ count: number; items: IptvGroup[] }> {
    const params = new URLSearchParams();
    if (sourceId) params.set("sourceId", sourceId);
    if (kind) params.set("kind", kind);
    return this.request(`/iptv/groups?${params.toString()}`);
  }

  iptvChannel(id: string, hours = 12): Promise<IptvChannelDetail> {
    return this.request(
      `/iptv/channels/${encodeURIComponent(id)}?hours=${hours}`,
    );
  }

  toggleIptvFavorite(id: string): Promise<{ favorite: boolean }> {
    return this.request(`/iptv/channels/${encodeURIComponent(id)}/favorite`, {
      method: "POST",
    });
  }

  iptvPlay(id: string): Promise<IptvPlaybackPlan> {
    return this.request(`/iptv/channels/${encodeURIComponent(id)}/play`);
  }

  /**
   * Tells the API how playback went. A decode failure moves the channel to the
   * server-side ffmpeg path so the next attempt works.
   */
  reportIptvPlayback(
    id: string,
    ok: boolean,
    reason?: string,
  ): Promise<{ mode: string | null }> {
    return this.request(`/iptv/channels/${encodeURIComponent(id)}/report`, {
      method: "POST",
      body: JSON.stringify({ ok, reason }),
    });
  }

  /** Media element source; it cannot send headers, so the session goes in the URL. */
  iptvStreamUrl(plan: IptvPlaybackPlan): string {
    const url = new URL(plan.url);
    url.searchParams.set("token", this.session.accessToken);
    url.searchParams.set("deviceId", this.session.deviceId);
    return url.toString();
  }
}
